import { bindErrorsToNgControl, bindTouchedToNgControl, UiFormFieldControl } from '@/ui/form-field/ui-form-field-control.class';
import {
    computed,
    Directive,
    type DoCheck,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    Input,
    type OnChanges,
    type OnDestroy,
    Renderer2,
    signal,
    type Signal,
    type SimpleChanges
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { FORM_FIELD } from '@angular/forms/signals';
import { MatInput } from '@angular/material/input';
import { merge, NEVER, Subject } from 'rxjs';

@Directive({
    selector: 'input[advizeoInput], textarea[advizeoInput]',
    providers: [{ provide: UiFormFieldControl, useExisting: AdvizeoInputDirective }],
    host: {
        '[disabled]': 'disabled()',
        '[readonly]': 'readonly',
        '[placeholder]': 'placeholder'
    }
})
export class AdvizeoInputDirective implements UiFormFieldControl, OnChanges, OnDestroy, DoCheck {
    ngControl = inject(NgControl, { optional: true, self: true })!;
    private _signalFormField = inject(FORM_FIELD, { optional: true });

    private _matInput: MatInput;
    private _internalStateChanges$ = new Subject<void>();

    dynamicHeight: Signal<boolean>;

    constructor() {
        const elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
        const renderer = inject(Renderer2);

        // Angular Material 22: MatInput resolves all of its dependencies through `inject()`,
        // so it must be instantiated from within an injection context and takes no arguments.
        this._matInput = new MatInput();
        this._matInput.required = false;
        renderer.addClass(elementRef.nativeElement, 'mdc-text-field__input');
        renderer.addClass(elementRef.nativeElement, 'advizeo-input');
        this.dynamicHeight = signal(elementRef.nativeElement.tagName === 'TEXTAREA');

        effect(() => {
            this._matInput.required = this.required();
        });

        effect(() => {
            this._matInput.disabled = this.disabled();
        });
    }

    required = input(false);
    disabled = input(false);
    touched = this._signalFormField
        ? computed(() => this._signalFormField?.state().touched() ?? false)
        : bindTouchedToNgControl(() => this.stateChanges);
    errors = this._signalFormField
        ? computed(() => this._signalFormField?.state().errors() ?? [])
        : bindErrorsToNgControl(() => this.stateChanges);

    @HostListener('blur') onBlur() {
        this._matInput._focusChanged(false);
    }

    @HostListener('focus') onFocus() {
        this._matInput._focusChanged(true);
    }

    @HostListener('input') onInput() {
        this._matInput._onInput();
    }

    @Input() placeholder: string = '';

    focus() {
        this._matInput.focus();
    }

    shouldLabelFloat() {
        return this._matInput.shouldLabelFloat;
    }

    focused() {
        return this._matInput.focused;
    }

    onContainerClick() {
        this._matInput.onContainerClick();
    }

    onClearRequest() {
        if (this.ngControl && this.ngControl.control) {
            this.ngControl.control.setValue('');
        }
    }

    @Input()
    get readonly() {
        return this._matInput.readonly;
    }
    set readonly(readonly: boolean) {
        this._matInput.readonly = readonly;
    }

    get stateChanges() {
        return merge(
            this._matInput.stateChanges,
            this.ngControl && this.ngControl.valueChanges ? this.ngControl.valueChanges : NEVER,
            this._internalStateChanges$
        );
    }

    isClearable = input(false, { alias: 'clearable' });

    clearable() {
        return this.isClearable() && this.ngControl && this.ngControl.control && this.ngControl.control.value;
    }

    // connect lifecycle events

    ngOnChanges(changes: SimpleChanges) {
        this._matInput.ngOnChanges();

        if (changes.disabled) {
            this._matInput.disabled = changes.disabled.currentValue;
        }
    }

    ngOnDestroy() {
        this._matInput.ngOnDestroy();
    }

    ngDoCheck() {
        this._matInput.ngDoCheck();
    }
}
